import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api.js';
import { Card, ErrorNote, Row, Skeleton } from './Home.js';
import { Link } from 'react-router';
import { PrimaryButton, Sheet, fieldStyle } from '../components/Sheet.js';

/**
 * Family members (docs/SCREEN-SPECS.md §10, SECURITY-ARCHITECTURE.md §5).
 *
 * Members are the spine of the model: a holding belongs to a member, and the
 * ingestion pipeline matches a statement to one by PAN. Without this screen the
 * whole app is unusable, which is why it comes first.
 *
 * PII is masked by DEFAULT and revealed only on demand — the server sends
 * `pan: "ABCDE••••F"` and never the real value until `/reveal` is called, so a
 * screenshot, a screen share, or a shoulder is not a disclosure. Reveal needs
 * admin, CSRF, and an authentication newer than ten minutes, and it writes an
 * audit event the whole family can see in Activity. That visibility is the
 * deterrent, not a warning dialog.
 */

interface MemberView {
  id: string;
  name: string;
  relation: string;
  pan: string | null;
  hasDob: boolean;
  uan: string | null;
  hasLogin: boolean;
  status: 'active' | 'erased';
  version: number;
}

export function Members() {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<MemberView | null>(null);
  const { data, isPending, error } = useQuery({
    queryKey: ['members'],
    queryFn: () => api.get<MemberView[]>('/members'),
  });

  if (isPending) return <Skeleton />;
  if (error) return <ErrorNote message={(error as Error).message} />;

  const active = data.filter((m) => m.status !== 'erased');

  return (
    <div>
      <h1 className="page-title">Family</h1>

      <Card title={`${active.length} member${active.length === 1 ? '' : 's'}`}>
        {active.length === 0 && (
          <p style={{ font: 'var(--type-subhead)', color: 'var(--color-label-secondary)', margin: 0 }}>
            No one here yet. Add yourself first — statements are matched to a member by PAN.
          </p>
        )}
        {active.map((m) => (
          <Row
            key={m.id}
            left={
              <button
                onClick={() => setEditing(m)}
                style={{
                  background: 'none', border: 'none', padding: 0, textAlign: 'left',
                  color: 'inherit', cursor: 'pointer', font: 'inherit',
                }}
              >
                <span className="row-title">{m.name}</span>
                <span className="row-meta">
                  {m.relation}
                  {m.pan && ` · PAN ${m.pan}`}
                  {!m.pan && ' · no PAN'}
                </span>
              </button>
            }
            right={
              <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                <MemberBadges member={m} />
                {/* One tap from Family to what this person actually owns. */}
                <Link
                  to={`/wealth?member=${m.id}`}
                  style={{ font: 'var(--type-footnote)', color: 'var(--color-tint)', textDecoration: 'none' }}
                >
                  Investments ›
                </Link>
              </span>
            }
          />
        ))}
      </Card>

      {/* One primary action per screen (Navigation Law 1), in the thumb zone. */}
      <PrimaryButton onClick={() => setAdding(true)} style={{ marginTop: 'var(--space-4)' }}>
        Add a family member
      </PrimaryButton>

      {adding && <MemberSheet onClose={() => setAdding(false)} />}
      {editing && <MemberSheet member={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function MemberBadges({ member }: { member: MemberView }) {
  const badges = [
    member.hasDob && 'DOB',
    member.uan && 'UAN',
    member.hasLogin && 'login',
  ].filter(Boolean) as string[];
  if (badges.length === 0) return <span style={{ color: 'var(--color-label-tertiary)' }}>—</span>;
  return (
    <span style={{ font: 'var(--type-caption)', color: 'var(--color-label-tertiary)' }}>
      {badges.join(' · ')}
    </span>
  );
}

/**
 * Add and edit share one sheet: the fields are identical, and two near-copies
 * would drift. Editing carries `version` for the optimistic-concurrency check —
 * the server rejects a stale write rather than silently overwriting a change
 * made on another device.
 */
function MemberSheet({ member, onClose }: { member?: MemberView; onClose: () => void }) {
  const isEdit = member !== undefined;
  const [name, setName] = useState(member?.name ?? '');
  const [relation, setRelation] = useState(member?.relation ?? '');
  const [pan, setPan] = useState('');
  const [dob, setDob] = useState('');
  const [uan, setUan] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: async () => {
      // Only send what was typed. An empty PAN field on the edit sheet means
      // "leave it alone", never "erase it" — the stored value is masked, so the
      // form cannot round-trip it and must not pretend to.
      const body: Record<string, unknown> = { name: name.trim(), relation: relation.trim() };
      if (pan.trim()) body['pan'] = pan.trim().toUpperCase();
      if (dob.trim()) body['dob'] = dob.trim();
      if (uan.trim()) body['uan'] = uan.trim();
      if (isEdit) {
        body['version'] = member.version;
        return api.put<MemberView>(`/members/${member.id}`, body);
      }
      return api.post<MemberView>('/members', body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onClose();
    },
    onError: (err) => {
      const e = err as ApiError;
      setFailure(
        e.code === 'CONFLICT'
          ? 'Someone changed this member on another device. Close and reopen to see the current values.'
          : e.message,
      );
    },
  });

  return (
    <Sheet title={isEdit ? `Edit ${member.name}` : 'Add a family member'} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setFailure(null);
          save.mutate();
        }}
      >
        <input
          style={fieldStyle}
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus={!isEdit}
        />
        <input
          style={fieldStyle}
          placeholder="Relation (self, spouse, daughter…)"
          value={relation}
          onChange={(e) => setRelation(e.target.value)}
          required
        />

        <p style={{ font: 'var(--type-footnote)', color: 'var(--color-label-secondary)', margin: '0 0 var(--space-2)' }}>
          {isEdit
            ? 'Leave a field blank to keep what is already stored. Stored values are encrypted and shown masked.'
            : 'PAN is how statements are matched to this person. Encrypted at rest, masked everywhere, never logged.'}
        </p>
        <input
          style={{ ...fieldStyle, textTransform: 'uppercase' }}
          placeholder={member?.pan ? `PAN (stored: ${member.pan})` : 'PAN (optional)'}
          value={pan}
          onChange={(e) => setPan(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
        <input
          style={fieldStyle}
          placeholder={member?.hasDob ? 'Date of birth (stored)' : 'Date of birth — YYYY-MM-DD (optional)'}
          value={dob}
          onChange={(e) => setDob(e.target.value)}
          autoComplete="off"
        />
        <input
          style={fieldStyle}
          placeholder={member?.uan ? `UAN (stored: ${member.uan})` : 'EPF UAN (optional)'}
          value={uan}
          onChange={(e) => setUan(e.target.value)}
          autoComplete="off"
        />

        {failure && (
          <p style={{ font: 'var(--type-subhead)', color: 'var(--color-status-critical)' }}>{failure}</p>
        )}

        <PrimaryButton type="submit" disabled={save.isPending || !name.trim() || !relation.trim()}>
          {save.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add member'}
        </PrimaryButton>
      </form>

      {isEdit && <RevealPanel member={member} />}
    </Sheet>
  );
}

/**
 * Step-up reveal. Deliberately NOT a toggle that silently shows everything:
 * each reveal is an explicit act, costs a round trip, and lands in the family's
 * Activity timeline. If the session is older than ten minutes the server
 * refuses with `reauth`, and the honest response is to say so rather than to
 * pretend the data is missing.
 */
function RevealPanel({ member }: { member: MemberView }) {
  const [revealed, setRevealed] = useState<{ pan: string | null; dob: string | null; uan: string | null } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const reveal = useMutation({
    mutationFn: () => api.post<{ pan: string | null; dob: string | null; uan: string | null }>(`/members/${member.id}/reveal`),
    onSuccess: setRevealed,
    onError: (err) => {
      const e = err as ApiError;
      setFailure(
        e.status === 401
          ? 'Sign in again to reveal identity details — this needs a session less than ten minutes old.'
          : e.message,
      );
    },
  });

  if (!member.pan && !member.hasDob && !member.uan) return null;

  return (
    <div style={{ marginTop: 'var(--space-6)', borderTop: '1px solid var(--color-separator)', paddingTop: 'var(--space-4)' }}>
      {revealed ? (
        <dl style={{ margin: 0, font: 'var(--type-subhead)' }}>
          {(['pan', 'dob', 'uan'] as const).map((k) =>
            revealed[k] ? (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
                <dt style={{ color: 'var(--color-label-secondary)' }}>{k.toUpperCase()}</dt>
                <dd style={{ margin: 0, fontVariantNumeric: 'tabular-nums' }}>{revealed[k]}</dd>
              </div>
            ) : null,
          )}
          <p style={{ font: 'var(--type-caption)', color: 'var(--color-label-tertiary)', margin: 'var(--space-3) 0 0' }}>
            This reveal was recorded in Activity.
          </p>
        </dl>
      ) : (
        <>
          <button
            onClick={() => {
              setFailure(null);
              reveal.mutate();
            }}
            disabled={reveal.isPending}
            style={{
              width: '100%', minHeight: 44, border: '1px solid var(--color-separator)',
              borderRadius: 'var(--radius-control)', background: 'transparent',
              color: 'var(--color-tint)', font: 'var(--type-subhead)', cursor: 'pointer',
            }}
          >
            {reveal.isPending ? 'Revealing…' : 'Reveal identity details'}
          </button>
          <p style={{ font: 'var(--type-caption)', color: 'var(--color-label-tertiary)', margin: 'var(--space-2) 0 0' }}>
            Everyone in the family can see reveals in Activity.
          </p>
        </>
      )}
      {failure && (
        <p style={{ font: 'var(--type-subhead)', color: 'var(--color-status-critical)' }}>{failure}</p>
      )}
    </div>
  );
}
